import React, { useState, useEffect, useCallback, useRef } from 'react'
import DeckGL from '@deck.gl/react'
import { H3HexagonLayer, TileLayer } from '@deck.gl/geo-layers'
import { IconLayer, BitmapLayer, LineLayer } from '@deck.gl/layers'
import { cellToLatLng, latLngToCell, cellToBoundary, cellToParent, cellToChildren, getResolution } from 'h3-js'

const INITIAL_VIEW_STATE = {
  longitude: 0,
  latitude: 0,
  zoom: 15,
  pitch: 60, // GPS navigation style tilt
  bearing: 0, // Facing north
  minZoom: 10,
  maxZoom: 20
}

// Function to calculate zoom level to fit H3 cell
const calculateZoomForH3Cell = (h3Index) => {
  try {
    const boundary = cellToBoundary(h3Index)
    if (boundary.length === 0) return 15
    
    // Calculate bounding box
    let minLat = Infinity, maxLat = -Infinity
    let minLng = Infinity, maxLng = -Infinity
    
    boundary.forEach(([lat, lng]) => {
      minLat = Math.min(minLat, lat)
      maxLat = Math.max(maxLat, lat)
      minLng = Math.min(minLng, lng)
      maxLng = Math.max(maxLng, lng)
    })
    
    // Calculate zoom level based on cell size
    const latDiff = maxLat - minLat
    const lngDiff = maxLng - minLng
    const maxDiff = Math.max(latDiff, lngDiff)
    
    // Approximate zoom level calculation
    // Resolution 7 H3 cells are roughly 0.5-1km across
    if (maxDiff > 0.01) return 12
    if (maxDiff > 0.005) return 13
    if (maxDiff > 0.002) return 14
    return 15
  } catch (error) {
    return 15
  }
}

// User location icon data URL (simple SVG circle)
const USER_ICON = 'data:image/svg+xml;base64,' + btoa(`
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" fill="#007AFF" stroke="#FFFFFF" stroke-width="2"/>
  </svg>
`)

// Function to find H3 cell that contains the user's location
const findContainingCell = (h3Index, userLat, userLng) => {
  let currentCell = h3Index
  
  // Check if user is in the current cell
  const resolution = getResolution(currentCell)
  const userCell = latLngToCell(userLat, userLng, resolution)
  
  if (currentCell === userCell) {
    return currentCell
  }
  
  // Try parent cells until we find one that contains the user or reach resolution 0
  let currentResolution = resolution
  while (currentResolution > 0) {
    try {
      const parentResolution = currentResolution - 1
      currentCell = cellToParent(currentCell, parentResolution)
      currentResolution = parentResolution
      
      // Check if user is in this parent cell
      const userCellAtResolution = latLngToCell(userLat, userLng, currentResolution)
      if (currentCell === userCellAtResolution) {
        return currentCell
      }
    } catch (error) {
      console.warn('Error getting parent cell:', error)
      // If we can't get parent, return the original cell
      break
    }
  }
  
  // If no parent contains the user, return the original cell
  return h3Index
}


function App() {
  const [viewState, setViewState] = useState(INITIAL_VIEW_STATE)
  const [h3Cell, setH3Cell] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [layers, setLayers] = useState([])
  const [locationHistory, setLocationHistory] = useState([])
  const [destinationCell, setDestinationCell] = useState(null)
  const [childCells, setChildCells] = useState([])
  const [userCell, setUserCell] = useState(null)
  const [hasArrived, setHasArrived] = useState(false)
  const [pathHistory, setPathHistory] = useState([]) // Track all H3 cells visited
  const animationRef = useRef(null)
  const deckRef = useRef(null)

  // Initialize global state for bearing animation
  useEffect(() => {
    window.th3re = {
      oldBearing: 0,
      newBearing: 0,
      lastCalculationTime: Date.now()
    }
  }, [])

  const getH3CellFromDNS = useCallback(async () => {
    try {
      const response = await fetch('https://cloudflare-dns.com/dns-query?name=cell.th3re.earth&type=TXT', {
        headers: {
          'accept': 'application/dns-json'
        }
      })
      
      if (!response.ok) {
        throw new Error(`DNS query failed: ${response.status}`)
      }
      
      const data = await response.json()
      
      console.log('DNS Response:', data)
      
      if (data.Status !== 0 || !data.Answer || data.Answer.length === 0) {
        throw new Error('No TXT record found')
      }
      
      // Extract H3 cell from TXT record (remove quotes)
      const rawData = data.Answer[0].data
      console.log('Raw DNS data:', rawData)
      
      const h3Index = rawData.replace(/"/g, '').trim()
      console.log('Processed H3 index:', h3Index)
      
      // Validate H3 cell before using it
      try {
        const resolution = getResolution(h3Index)
        if (resolution < 0 || resolution > 15) {
          throw new Error(`Invalid H3 resolution: ${resolution}`)
        }
        
        const h3Center = cellToLatLng(h3Index)
        
        setDestinationCell(h3Index)
        setH3Cell({
          index: h3Index,
          center: h3Center
        })
      } catch (validationError) {
        throw new Error(`Invalid H3 cell from DNS: ${h3Index} - ${validationError.message}`)
      }
      
    } catch (err) {
      setError('Error fetching H3 cell from DNS: ' + err.message)
      setLoading(false)
    }
  }, [])

  const getCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by this browser.')
      setLoading(false)
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords
        
        // Just get user location for arrow, H3 cell comes from DNS
        setLoading(false)
      },
      (error) => {
        let errorMessage = 'Unable to retrieve your location'
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied by user'
            break
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information is unavailable'
            break
          case error.TIMEOUT:
            errorMessage = 'Location request timed out'
            break
        }
        setError(errorMessage)
        setLoading(false)
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    )
  }, [])

  useEffect(() => {
    // Get H3 cell from DNS
    getH3CellFromDNS()
    
    // Get user location for arrow
    getCurrentLocation()
    
    // Update H3 cell from DNS every minute
    const dnsInterval = setInterval(() => {
      getH3CellFromDNS()
    }, 60000) // 60 seconds
    
    return () => clearInterval(dnsInterval)
  }, [getH3CellFromDNS, getCurrentLocation])


  // Bearing calculation effect - recalculate every second
  useEffect(() => {
    if (locationHistory.length >= 2) {
      const interval = setInterval(() => {
        // Calculate movement direction from oldest to newest point
        const oldest = locationHistory[0]
        const newest = locationHistory[locationHistory.length - 1]
        
        // Calculate bearing from oldest to newest point
        const deltaLng = newest.lng - oldest.lng
        const deltaLat = newest.lat - oldest.lat
        
        // Calculate distance moved
        const distance = Math.sqrt(deltaLat * deltaLat + deltaLng * deltaLng)
        
        // Thresholds
        const MIN_DISTANCE = 0.00001 // Minimum movement threshold
        const BBOX_THRESHOLD = 0.00000001 // Minimum bounding box area
        
        // Calculate bounding box area
        const minLat = Math.min(...locationHistory.map(p => p.lat))
        const maxLat = Math.max(...locationHistory.map(p => p.lat))
        const minLng = Math.min(...locationHistory.map(p => p.lng))
        const maxLng = Math.max(...locationHistory.map(p => p.lng))
        const bbox = (maxLat - minLat) * (maxLng - minLng)
        
        let calculatedBearing = 0
        if (distance >= MIN_DISTANCE && bbox >= BBOX_THRESHOLD) {
          // Valid movement detected - calculate bearing
          calculatedBearing = Math.atan2(deltaLng, deltaLat) * (180 / Math.PI)
        }
        
        // Update global bearing values for interpolation
        window.th3re.oldBearing = window.th3re.newBearing
        window.th3re.newBearing = calculatedBearing
        window.th3re.lastCalculationTime = Date.now()
      }, 1000)
      
      return () => clearInterval(interval)
    }
  }, [locationHistory])

  // Simple animation loop that runs continuously
  useEffect(() => {
    const animateBearing = () => {
      const currentTime = Date.now()
      const timeDelta = currentTime - window.th3re.lastCalculationTime
      
      let tweenedBearing
      
      if (timeDelta < 1000) {
        // Interpolate between oldBearing and newBearing
        const progress = timeDelta / 1000
        
        // Handle bearing wraparound (0-360 degrees)
        let bearingDiff = window.th3re.newBearing - window.th3re.oldBearing
        if (bearingDiff > 180) bearingDiff -= 360
        if (bearingDiff < -180) bearingDiff += 360
        
        tweenedBearing = window.th3re.oldBearing + (bearingDiff * progress)
      } else {
        // Animation complete - use the new bearing
        tweenedBearing = window.th3re.newBearing
      }
      
      // Update map view bearing
      setViewState(prev => ({
        ...prev,
        bearing: tweenedBearing
      }))
      
      // Continue animation
      animationRef.current = requestAnimationFrame(animateBearing)
    }
    
    // Start animation
    animationRef.current = requestAnimationFrame(animateBearing)
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
        animationRef.current = null
      }
    }
  }, [])

  // Create map layer only (no parent hexagon rendering)
  useEffect(() => {
    if (!loading && !error) {
      // Create map tile layer
      const mapLayer = new TileLayer({
        id: 'map-layer',
        data: 'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png',
        minZoom: 0,
        maxZoom: 19,
        tileSize: 256,
        renderSubLayers: props => {
          const { tile, data } = props
          const { boundingBox } = tile
          return new BitmapLayer({
            id: props.id,
            data: null,
            image: data,
            bounds: [boundingBox[0][0], boundingBox[0][1], boundingBox[1][0], boundingBox[1][1]]
          })
        }
      })
      
      // Keep existing user layer if it exists
      setLayers(prev => {
        const existingUser = prev.find(layer => layer.id === 'user-layer')
        if (existingUser) {
          return [mapLayer, existingUser]
        }
        return [mapLayer]
      })
    }
  }, [loading, error])

  // Create arrow layer and track location for bearing calculation (throttled to once per second)
  useEffect(() => {
    if (!loading && !error) {
      let lastUpdateTime = 0
      const UPDATE_INTERVAL = 1000 // 1 second
      
      const watchId = navigator.geolocation.watchPosition(
        (position) => {
          const now = Date.now()
          
          // Throttle updates to once per second
          if (now - lastUpdateTime < UPDATE_INTERVAL) {
            return
          }
          lastUpdateTime = now
          
          const { latitude, longitude } = position.coords
          const timestamp = now
          
          // Add new location to history for bearing calculation
          setLocationHistory(prev => {
            const newHistory = [...prev, { lat: latitude, lng: longitude, timestamp }]
            // Keep only last 30 seconds
            return newHistory.filter(point => timestamp - point.timestamp <= 30000)
          })
          
          // Track H3 cell for path history (always track at resolution 7 for consistency)
          try {
            const currentH3Cell = latLngToCell(latitude, longitude, 7)
            
            // Add to path history if it's a new cell
            setPathHistory(prev => {
              const lastCell = prev[prev.length - 1]
              if (!lastCell || lastCell !== currentH3Cell) {
                return [...prev, currentH3Cell]
              }
              return prev
            })
          } catch (error) {
            console.warn('Error tracking H3 cell for path:', error)
          }
          
          // Create user location icon layer
          const userLayer = new IconLayer({
            id: 'user-layer',
            data: [{
              position: [longitude, latitude]
            }],
            iconAtlas: USER_ICON,
            iconMapping: {
              marker: {
                x: 0,
                y: 0,
                width: 24,
                height: 24,
                anchorX: 12,
                anchorY: 12
              }
            },
            getIcon: () => 'marker',
            getPosition: d => d.position,
            getSize: 24,
            sizeScale: 1,
            billboard: false, // Render in world space, not screen space
            pickable: false
          })
          
          // Add user location to existing layers
          setLayers(prev => {
            const existingLayers = prev.filter(layer => layer.id !== 'user-layer')
            return [...existingLayers, userLayer]
          })
          
        },
        (error) => {
          console.warn('Location update failed:', error)
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 1000 }
      )
      
      return () => navigator.geolocation.clearWatch(watchId)
    }
  }, [loading, error])

  // Handle viewport calculations when location history changes
  useEffect(() => {
    if (locationHistory.length > 0 && h3Cell) {
      const latestLocation = locationHistory[locationHistory.length - 1]
      const userLat = latestLocation.lat
      const userLng = latestLocation.lng
      
      // Find the containing cell (original or parent) - don't update state, just calculate
      console.log('Finding containing cell for:', h3Cell.index, 'user at:', userLat, userLng)
      const containingCell = findContainingCell(h3Cell.index, userLat, userLng)
      console.log('Found containing cell:', containingCell)
      const containingCellCenter = cellToLatLng(containingCell)
      
      // Only update H3 cell state if it actually changed
      if (containingCell !== h3Cell.index) {
        setH3Cell(prev => ({
          ...prev,
          index: containingCell,
          center: containingCellCenter
        }))
      }
      
      // Calculate bounding box that includes both user location and H3 cell boundary
      const cellBoundary = cellToBoundary(containingCell)
      
      // Find min/max coordinates for the H3 cell boundary
      let minLat = Infinity, maxLat = -Infinity
      let minLng = Infinity, maxLng = -Infinity
      
      cellBoundary.forEach(([lat, lng]) => {
        minLat = Math.min(minLat, lat)
        maxLat = Math.max(maxLat, lat)
        minLng = Math.min(minLng, lng)
        maxLng = Math.max(maxLng, lng)
      })
      
      // Include user location in bounding box
      minLat = Math.min(minLat, userLat)
      maxLat = Math.max(maxLat, userLat)
      minLng = Math.min(minLng, userLng)
      maxLng = Math.max(maxLng, userLng)
      
      // Add more substantial padding to ensure full visibility
      const latPadding = (maxLat - minLat) * 0.4
      const lngPadding = (maxLng - minLng) * 0.4
      
      minLat -= latPadding
      maxLat += latPadding
      minLng -= lngPadding
      maxLng += lngPadding
      
      // Calculate optimal zoom accounting for viewport aspect ratio
      const findOptimalZoom = (centerLat, centerLng, bounds) => {
        // Calculate the span of our bounding box
        const latSpan = bounds.maxLat - bounds.minLat
        const lngSpan = bounds.maxLng - bounds.minLng
        
        // Get viewport dimensions
        const viewportWidth = window.innerWidth
        const viewportHeight = window.innerHeight
        const aspectRatio = viewportWidth / viewportHeight
        
        // Determine which dimension is the limiting factor
        let limitingSpan, limitingDimension
        
        if (aspectRatio > 1) {
          // Landscape: width is wider, height is limiting factor
          limitingSpan = latSpan
          limitingDimension = 'height'
        } else {
          // Portrait: height is taller, width is limiting factor
          limitingSpan = lngSpan
          limitingDimension = 'width'
        }
        
        // For portrait mode, we need to be more conservative with longitude span
        // because longitude degrees get narrower as you move away from equator
        const latitudeAdjustment = Math.cos(centerLat * Math.PI / 180)
        
        if (limitingDimension === 'width') {
          // In portrait mode, adjust longitude span for latitude
          limitingSpan = lngSpan / latitudeAdjustment
        }
        
        // We want our bounding box to fit in about 75% of the viewport (more conservative)
        const targetSpan = limitingSpan / 0.75
        
        // Calculate zoom level needed
        const zoom = Math.log2(360 / targetSpan)
        
        // Return the calculated zoom without clamping
        return Math.round(zoom)
      }
      
      const optimalZoom = findOptimalZoom(userLat, userLng, {
        minLat, maxLat, minLng, maxLng
      })
      
      // Update view state to center on user location with optimal zoom
      setViewState(prev => ({
        ...prev,
        longitude: userLng,
        latitude: userLat,
        zoom: optimalZoom
      }))
    }
  }, [locationHistory, h3Cell])

  // Calculate child cells and user cell when H3 cell changes
  useEffect(() => {
    if (h3Cell && destinationCell && locationHistory.length > 0) {
      const { index: containingCell } = h3Cell
      
      try {
        // Validate the H3 cell first
        if (!containingCell || typeof containingCell !== 'string' || containingCell.length === 0) {
          console.warn('Invalid H3 cell:', containingCell)
          setChildCells([])
          setUserCell(null)
          setHasArrived(false)
          return
        }
        
        console.log('Containing cell:', containingCell)
        console.log('Destination cell:', destinationCell)
        
        // Check if the containing cell can have children (resolution < 15)
        const resolution = getResolution(containingCell)
        console.log('Containing cell resolution:', resolution)
        
        if (resolution < 0 || resolution > 15) {
          console.warn('Invalid H3 resolution:', resolution, 'for cell:', containingCell)
          setChildCells([])
          setUserCell(null)
          setHasArrived(false)
          return
        }
        
        if (resolution >= 15) {
          // At maximum resolution, no children possible
          console.log('At maximum resolution, no children possible')
          setChildCells([])
          setUserCell(null)
          setHasArrived(false)
          return
        }
        
        
        // Get the 7 child cells at the next resolution level
        const childResolution = resolution + 1
        console.log('Getting children for cell:', containingCell, 'at resolution:', resolution, 'target child resolution:', childResolution)
        
        if (childResolution > 15) {
          console.log('Child resolution would exceed maximum (15)')
          setChildCells([])
          setUserCell(null)
          setHasArrived(false)
          return
        }
        
        const children = cellToChildren(containingCell, childResolution)
        console.log('Got children:', children)
        setChildCells(children)
        
        // Find which child cell the user is in
        const userLat = locationHistory[locationHistory.length - 1].lat
        const userLng = locationHistory[locationHistory.length - 1].lng
        
        if (childResolution > 15) {
          // Can't create child cells at resolution > 15
          setUserCell(null)
          setHasArrived(false)
          return
        }
        
        const userCellIndex = latLngToCell(userLat, userLng, childResolution)
        setUserCell(userCellIndex)
        
        // Check if user has arrived at the exact destination
        if (userCellIndex === destinationCell) {
          setHasArrived(true)
        } else {
          setHasArrived(false)
        }
      } catch (error) {
        console.warn('Error calculating child cells:', error)
        // Clear child cells if there's an error
        setChildCells([])
        setUserCell(null)
        setHasArrived(false)
      }
    } else {
      // Clear child cells if we don't have the required data
      setChildCells([])
      setUserCell(null)
      setHasArrived(false)
    }
  }, [h3Cell, destinationCell, locationHistory])

  // Create child cell layers
  useEffect(() => {
    if (childCells.length > 0 && userCell && destinationCell && !loading && !error) {
      try {
        const childLayers = childCells.map((childIndex, i) => {
          const isUserInThisCell = childIndex === userCell
          const isDestination = childIndex === destinationCell
          
          let color
          if (isDestination) {
            // Destination cell - pulsing green when arrived, blue otherwise
            color = hasArrived ? [0, 255, 0, 150] : [135, 206, 250, 100]
          } else if (isUserInThisCell) {
            // User is in this cell - red (wrong cell, not the destination)
            color = [220, 20, 60, 100]
          } else {
            // Other cells - blue (potential correct path)
            color = [135, 206, 250, 100]
          }
          
          // Determine line color based on fill color
          let lineColor
          if (isDestination && hasArrived) {
            // Green destination - green border
            lineColor = [0, 200, 0, 255]
          } else if (isUserInThisCell) {
            // Red user cell - red border
            lineColor = [180, 0, 0, 255]
          } else {
            // Blue cells - blue border
            lineColor = [70, 130, 180, 255]
          }
          
          return new H3HexagonLayer({
            id: `child-cell-${i}`,
            data: [{
              hexagon: childIndex,
              center: cellToLatLng(childIndex)
            }],
            getHexagon: d => d.hexagon,
            getFillColor: color,
            getLineColor: lineColor,
            lineWidthMinPixels: 2,
            elevationScale: 0,
            extruded: false
          })
        })
        
        // Update layers to include child cells
        setLayers(prev => {
          const existingLayers = prev.filter(layer => !layer.id.startsWith('child-cell-'))
          return [...existingLayers, ...childLayers]
        })
      } catch (error) {
        console.warn('Error creating child cell layers:', error)
        // Remove any existing child cell layers
        setLayers(prev => prev.filter(layer => !layer.id.startsWith('child-cell-')))
      }
    } else {
      // Remove child cell layers if we don't have the required data
      setLayers(prev => prev.filter(layer => !layer.id.startsWith('child-cell-')))
    }
  }, [childCells, userCell, destinationCell, hasArrived, loading, error])

  // Create path line layer - always show if we have path history
  useEffect(() => {
    if (pathHistory.length > 1) {
      try {
        // Convert H3 cells to lat/lng coordinates for the line
        const pathCoordinates = pathHistory.map(h3Cell => {
          const [lng, lat] = cellToLatLng(h3Cell)
          return [lng, lat]
        })
        
        const pathLayer = new LineLayer({
          id: 'path-layer',
          data: [{
            path: pathCoordinates,
            color: [135, 206, 250, 180] // Light blue with some transparency
          }],
          getPath: d => d.path,
          getColor: d => d.color,
          widthMinPixels: 3,
          widthMaxPixels: 6,
          capRounded: true,
          jointRounded: true,
          pickable: false
        })
        
        // Add path layer to existing layers
        setLayers(prev => {
          const existingLayers = prev.filter(layer => layer.id !== 'path-layer')
          return [...existingLayers, pathLayer]
        })
      } catch (error) {
        console.warn('Error creating path layer:', error)
      }
    } else {
      // Remove path layer if no path history
      setLayers(prev => prev.filter(layer => layer.id !== 'path-layer'))
    }
  }, [pathHistory])

  const handleViewStateChange = ({ viewState }) => {
    setViewState(viewState)
  }

  if (loading) {
    return (
      <div className="loading">
        <div>Getting your location...</div>
        <div style={{ fontSize: '14px', marginTop: '10px', opacity: 0.7 }}>
          Please allow location access
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="error">
        <div>{error}</div>
        <button 
          onClick={getCurrentLocation}
          style={{
            marginTop: '15px',
            padding: '10px 20px',
            backgroundColor: '#007AFF',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            fontSize: '16px',
            cursor: 'pointer'
          }}
        >
          Try Again
        </button>
      </div>
    )
  }

  return (
    <div style={{ width: '100vw', height: '100vh', position: 'relative' }}>
      {/* Simple map-like background */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
        zIndex: 0
      }} />
      
      <DeckGL
        ref={deckRef}
        initialViewState={viewState}
        controller={false}
        onViewStateChange={handleViewStateChange}
        layers={layers}
        style={{ position: 'relative', zIndex: 1 }}
        getTooltip={({ object }) => object ? `H3 Cell: ${object.hexagon}` : null}
      />
      
      {/* Header Message */}
      <div style={{
        position: 'absolute',
        top: '20px',
        left: '20px',
        right: '20px',
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        color: 'white',
        padding: '15px',
        borderRadius: '8px',
        fontSize: '18px',
        textAlign: 'center',
        zIndex: 1000
      }}>
        {hasArrived ? (
          <span style={{ 
            color: '#00FF00',
            animation: 'pulse 1s ease-in-out infinite alternate'
          }}>
            You're <strong>h3</strong>re!
          </span>
        ) : (
          <span>
            Find out what's <em>t<strong>h3</strong>re</em>
          </span>
        )}
      </div>
    </div>
  )
}

export default App